/**
 * 월패드(HT) 지도 생성 서비스.
 *
 * 백엔드가 헤드리스 크롬으로 현장 맵을 고정 캔버스(2168x1528 = 542x382 의 4배)에
 * 렌더해서 층별 PNG 와 HT_MAP_TRANSFORM 계수를 한 번에 만든다. 그린 변환이 곧
 * 계수라서 별도 피팅이 없다 — 대신 **이미지와 계수는 한 세트**이므로 지도를 다시
 * 뽑으면 HT 등록 이미지와 ht_location 의 env 계수를 반드시 함께 교체해야 한다.
 */
import { createAuthenticatedClient } from './api/apiClient';

// 층마다 크롬 렌더 + 대기 + 스크린샷이 들어간다(2개 층에 40초 안팎).
// 백엔드 타임아웃 600s / nginx proxy_read_timeout 600s 보다 넉넉히 잡는다.
const api = createAuthenticatedClient({ timeout: 900000 });

/** 542x382 픽셀 기준 좌표 계수. ht_location 의 HT_MAP_TRANSFORM 에 그대로 들어간다. */
export interface WallpadMapTransform {
  scaleX: number;
  scaleY: number;
  offsetX: number;
  offsetY: number;
}

export interface WallpadMapFloor {
  /** 층 이름. HT 지도번호와 일치시켜야 한다. */
  name: string;
  transform: WallpadMapTransform;
  /** HT 등록용 542x382 PNG (data URL) */
  ht_png: string;
  /** 고정 원본 2168x1528 PNG (data URL) */
  orig_png: string;
  /**
   * 생성기의 CCTV 전수 검증 수치를 읽었는지 여부.
   * false 면 수치를 0 으로 보여주면 안 된다 — 모르는 것을 정상으로 부르지 않는다.
   */
  validated: boolean;
  cctv_count: number;
  /** 1px 를 넘으면 뭔가 잘못된 것이다. */
  max_error_px: number;
  /** 캔버스 밖으로 예측된 CCTV 수. 0 이어야 한다. */
  out_of_range: number;
}

export interface WallpadMapResult {
  map_url: string;
  parking_lot_id: number;
  floors: WallpadMapFloor[];
  /** ht_location .env 에 붙여넣을 한 줄. */
  transform_env: string;
  /** 생성기 원문 로그 (진단용). */
  log: string;
}

interface GenerateResponse {
  success: boolean;
  message: string;
  result?: WallpadMapResult;
}

export class WallpadMapService {
  /**
   * 맵 주소와 주차장 ID 로 층별 월패드 이미지·계수를 생성한다.
   * 실패는 예외로 던진다 (백엔드가 사유를 message 로 준다).
   */
  static async generate(mapUrl: string, parkingLotId: number): Promise<WallpadMapResult> {
    const response = await api.post<GenerateResponse>('/v0.1/wallpad-map/generate', {
      map_url: mapUrl,
      parking_lot_id: parkingLotId,
    });
    const data = response.data;
    if (!data.success || !data.result) {
      throw new Error(data.message || '월패드 지도 생성 실패');
    }
    return data.result;
  }
}

export default WallpadMapService;
